import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ApiError, get, onAuthFailure } from "./api.ts";
import type { PlanDetail } from "./types.ts";

/** Every plan-scoped query key starts with ["plan", planId] so cutoff/logout can drop them together. */
export const planKey = (planId: string, ...rest: unknown[]) => ["plan", planId, ...rest] as const;

const Ctx = createContext<PlanDetail | null>(null);
export function usePlan(): PlanDetail {
  const p = useContext(Ctx);
  if (!p) throw new Error("usePlan outside PlanProvider");
  return p;
}
export const PlanContext = Ctx;

export function usePlanQuery(planId: string) {
  return useQuery({ queryKey: planKey(planId, "detail"), queryFn: async () => (await get<{ data: PlanDetail }>(`/plans/${planId}`)).data });
}

/** On cutoff or revocation anywhere in the plan: unmount protected data and show the access page. */
export function usePlanAccessListener(planId: string) {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  useEffect(
    () =>
      onAuthFailure((err: ApiError) => {
        if (err.code === "PLAN_ACCESS_EXPIRED" || err.code === "MEMBERSHIP_REVOKED") {
          qc.removeQueries({ queryKey: ["plan", planId] });
          navigate(`/planos/${planId}/acesso-encerrado${err.code === "MEMBERSHIP_REVOKED" ? "?motivo=removido" : ""}`, { replace: true });
        }
      }),
    [qc, navigate, planId],
  );
}

export function PlanProvider({ plan, children }: { plan: PlanDetail; children: ReactNode }) {
  return <Ctx.Provider value={plan}>{children}</Ctx.Provider>;
}
